import { getStripeConnectCountryCode } from "../../constants/stripeConnectCountries";

export const ONBOARDING_COUNTRY_REQUIRED_MESSAGE =
    "Select your country to continue verification.";
export const ONBOARDING_UNSUPPORTED_COUNTRY_MESSAGE =
    "This country is not yet supported for payouts. Please use a supported country.";

export const PINNED_COUNTRY_CODE = "FR";
export const TOP_USED_COUNTRY_CODES = ["FR", "US", "DE", "GB"];

/**
 * Profile completion steps: name (0), picture (1), bio (2), and — for
 * creators only — payout setup (3). The payout step collects just a country;
 * Stripe's embedded onboarding gathers everything else itself.
 */
export const getCompleteProfileStepConfig = (hasStripeStep) => {
    const stripeStep = hasStripeStep ? 3 : null;
    const finalStep = stripeStep ?? 2;

    return {
        stripeStep,
        finalStep,
        titles: hasStripeStep
            ? ["Add your full name", "Add a profile picture", "Add a cool bio", "Set up payouts"]
            : ["Add your full name", "Add a profile picture", "Add a cool bio"],
    };
};

export const orderCountriesWithTopUsed = (countries = []) => {
    const topCodes = new Set(TOP_USED_COUNTRY_CODES.map((code) => code.toUpperCase()));
    const byCode = new Map(
        countries.map((country) => [(country.code || "").toUpperCase(), country])
    );

    const france = byCode.get(PINNED_COUNTRY_CODE);
    const otherTopCountries = TOP_USED_COUNTRY_CODES
        .filter((code) => code.toUpperCase() !== PINNED_COUNTRY_CODE)
        .map((code) => byCode.get(code))
        .filter(Boolean);

    const rest = countries
        .filter((country) => !topCodes.has((country.code || "").toUpperCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

    return [...(france ? [france] : []), ...otherTopCountries, ...rest];
};

/**
 * Infers an ISO alpha-2 region from a BCP 47 locale tag (e.g. "fr-FR" → "FR",
 * "en" → "US" via likely-subtag maximization). Used only to pre-select the
 * payout country — the user always confirms before an account is created.
 */
export const getRegionFromLocale = (locale) => {
    if (typeof locale !== "string" || !locale.trim()) return null;
    try {
        const intlLocale = new Intl.Locale(locale.trim());
        const region = intlLocale.region || intlLocale.maximize().region;
        return region && /^[A-Z]{2}$/.test(region) ? region : null;
    } catch {
        return null;
    }
};

export const buildCompleteOnboardingPayload = (address) => {
    if (!address) return {};

    const country = String(address.country || "").trim();
    const line1 = String(address.address_line_1 || "").trim();
    const line2 = String(address.address_line_2 || "").trim();
    const city = String(address.city || "").trim();
    const postalCode = String(address.postal_code || "").trim();

    const stripeAddress = {
        ...(line1 ? { address_line_1: line1 } : {}),
        ...(line2 ? { address_line_2: line2 } : {}),
        ...(city ? { city } : {}),
        ...(postalCode ? { postal_code: postalCode } : {}),
        ...(country ? { country } : {}),
    };

    return {
        ...(country ? { country } : {}),
        ...(Object.keys(stripeAddress).length ? { address: stripeAddress } : {}),
    };
};

export const getOnboardingErrorMessage = (errorLike) => {
    const code = errorLike?.code || errorLike?.response?.code;
    if (code === "COUNTRY_REQUIRED") return ONBOARDING_COUNTRY_REQUIRED_MESSAGE;
    if (code === "UNSUPPORTED_COUNTRY") return ONBOARDING_UNSUPPORTED_COUNTRY_MESSAGE;

    // response.message carries the backend's reason; a thrown fetch error's
    // own .message is a generic placeholder, so it comes last.
    return (
        errorLike?.response?.message ||
        errorLike?.message ||
        "Unable to start verification right now."
    );
};

/**
 * Builds the Stripe onboarding payload from a stored main address, resolving
 * its `country` (which may be a display name like "France") to an ISO alpha-2
 * code via the countries list. Returns null when there's no address or the
 * country can't be resolved to a code — callers must then block account
 * creation and prompt the user to fix their address, since a Stripe account's
 * country is immutable once created.
 */
export const buildOnboardingPayloadFromAddress = (mainAddress, countries = []) => {
    if (!mainAddress) return null;
    const countryCode = getStripeConnectCountryCode(mainAddress.country, countries);
    if (!countryCode) return null;
    return buildCompleteOnboardingPayload({ ...mainAddress, country: countryCode });
};
